import { createContext, useContext, useState, useEffect } from 'react'
import api from '../lib/api'

const AuthContext = createContext(null)
const STORAGE_KEYS = { USER: 'synxreach_user', TOKEN: 'access_token', REMEMBER: 'synxreach_remember' }

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const bootstrapAuth = async () => {
      try {
        // Check for token in URL params (redirect from marketing site)
        const params = new URLSearchParams(window.location.search)
        const urlToken = params.get('token')

        if (urlToken) {
          localStorage.setItem(STORAGE_KEYS.TOKEN, urlToken)
          // Clean token from URL
          window.history.replaceState({}, '', window.location.pathname)

          // Fetch user profile with the token
          try {
            const meRes = await api.get('/auth/me', {
              headers: { Authorization: `Bearer ${urlToken}` },
            })
            const me = meRes.data
            const userData = {
              id: me.id,
              email: me.email,
              name: me.name || me.username || me.email,
              role: me.role || 'user',
              workspace_id: me.workspace_id || null,
              workspace_role: me.workspace_role || 'Editor',
              workspace_name: me.workspace_name || null,
              avatar: me.avatar || null,
              company: me.company || null,
              timezone: me.timezone || 'America/New_York',
              language: me.language || 'en',
            }
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData))
            setUser(userData)
          } catch {
            localStorage.removeItem(STORAGE_KEYS.TOKEN)
          }
        } else {
          const storedUser = localStorage.getItem(STORAGE_KEYS.USER)
          const storedToken = localStorage.getItem(STORAGE_KEYS.TOKEN)
          if (storedUser && storedToken) setUser(JSON.parse(storedUser))
        }
      } catch (error) {
        console.error('Error checking auth:', error)
        localStorage.removeItem(STORAGE_KEYS.USER)
        localStorage.removeItem(STORAGE_KEYS.TOKEN)
      } finally {
        setIsLoading(false)
      }
    }

    bootstrapAuth()
  }, [])

  const _finalizeSession = async (token, remember, fallbackEmail) => {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token)
    const meRes = await api.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
    const me = meRes.data
    const userData = {
      id: me.id,
      email: me.email || fallbackEmail,
      name: me.name || me.username || fallbackEmail,
      role: me.role || 'user',
      workspace_id: me.workspace_id || null,
      workspace_role: me.workspace_role || 'Editor',
      workspace_name: me.workspace_name || null,
      avatar: me.avatar || null,
      company: me.company || null,
      timezone: me.timezone || 'America/New_York',
      language: me.language || 'en',
    }
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData))
    if (remember) localStorage.setItem(STORAGE_KEYS.REMEMBER, 'true')
    setUser(userData)
    return { success: true, user: userData }
  }

  const login = async (username, password, remember = false) => {
    const formData = new URLSearchParams()
    formData.append('username', username)
    formData.append('password', password)

    const res = await api.post('/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })

    if (res.data?.requires_2fa) {
      return { requires_2fa: true, mfa_token: res.data.mfa_token }
    }

    const token = res.data.access_token || res.data.token
    if (!token) {
      return { success: false, error: 'Token not returned from API' }
    }
    return await _finalizeSession(token, remember, username)
  }

  const completeTwoFactor = async (mfa_token, code, remember = false) => {
    const res = await api.post('/auth/2fa/login-verify', { mfa_token, code })
    const token = res.data?.access_token
    if (!token) return { success: false, error: 'Token not returned' }
    return await _finalizeSession(token, remember, res.data?.user?.email)
  }

  const logout = () => {
    setUser(null)
    localStorage.removeItem(STORAGE_KEYS.USER)
    localStorage.removeItem(STORAGE_KEYS.TOKEN)
    localStorage.removeItem(STORAGE_KEYS.REMEMBER)
  }

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates }
    setUser(updatedUser)
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser))
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated: !!user, login, logout, updateUser, completeTwoFactor }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}

export default AuthContext
